import { Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../services/auth.service';

/**
 * Modal de "tu sesión está por expirar" con countdown.
 * - Aparece cuando `AuthService.expiryModal()` deja de ser null.
 * - Muestra `mm:ss` decreciendo en tiempo real.
 * - Botón "Continuar" → llama `extenderSesion()` (refresh del JWT).
 * - Botón "Cerrar sesión" → logout manual.
 * - Si el countdown llega a 0 sin acción → el `_expireTimer` del AuthService
 *   dispara el handleSessionExpired y este componente se oculta automático.
 *
 * Se monta a nivel `AppComponent` para que aparezca encima de toda la app.
 */
@Component({
  selector: 'app-expiry-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './expiry-modal.component.html',
  styleUrls: ['./expiry-modal.component.scss'],
})
export class ExpiryModalComponent implements OnDestroy {
  private auth = inject(AuthService);

  /** Tick interno cada segundo cuando el modal está abierto. */
  private tickInterval: ReturnType<typeof setInterval> | null = null;
  /** Segundos a mostrar — copia local del signal del AuthService, decreciendo. */
  segundosRestantes = signal(0);

  /** Loading visible mientras llama a /auth/extend. */
  cargando = signal(false);

  esVisible = computed(() => this.auth.expiryModal() !== null);

  /** mm:ss en pantalla. */
  formato = computed(() => {
    const s = Math.max(0, this.segundosRestantes());
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  });

  constructor() {
    // Cuando el AuthService abre el modal, copiamos el contador y arrancamos
    // el tick. Cuando lo cierra (extend OK o logout), detenemos el tick.
    effect(() => {
      const modal = this.auth.expiryModal();
      if (modal) {
        this.segundosRestantes.set(modal.segundosRestantes);
        this.startTick();
      } else {
        this.stopTick();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopTick();
  }

  private startTick(): void {
    this.stopTick();
    this.tickInterval = setInterval(() => {
      this.segundosRestantes.update(v => Math.max(0, v - 1));
    }, 1000);
  }

  private stopTick(): void {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  async continuar(): Promise<void> {
    if (this.cargando()) return;
    this.cargando.set(true);
    try {
      await this.auth.extenderSesion();
    } finally {
      this.cargando.set(false);
    }
  }

  cerrarSesion(): void {
    void this.auth.logOut();
  }
}
